import { ReactElement } from "react";

import { getExtensionValue } from "@medplum/core";
import { CodeableConcept, Reference, Resource } from "@medplum/fhirtypes";
import { QuestionnaireItemType } from "@medplum/react";

import { Checkbox, NumberInput } from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";

import { IconCalendar, IconClock, IconSearch } from "@tabler/icons-react";
import { parseISO } from "date-fns";

import { Extensions } from "@/fhir/enums/extensions";
import { questionnaireFieldTestId } from "@/intake-forms/utils/questionnaire-field-testid";
import { getQuestionnaireReferenceFieldConfig } from "@/intake-forms/utils/questionnaire-reference-field-config";
import { GenericFormValues } from "@/utils/questionnaires";

import EnableWhenWrapper from "../components/EnableWhenWrapper";
import QuestionnaireAttachmentField from "../components/QuestionnaireAttachmentField";
import QuestionnaireChoiceField from "../components/QuestionnaireChoiceField";
import QuestionnaireDateField from "../components/QuestionnaireDateField";
import QuestionnaireDateTimeField from "../components/QuestionnaireDateTimeField";
import QuestionnaireDecimalField from "../components/QuestionnaireDecimalField";
import QuestionnaireDisplay from "../components/QuestionnaireDisplay";
import QuestionnaireGroupField from "../components/QuestionnaireGroupField";
import QuestionnaireReferenceField from "../components/QuestionnaireReferenceField";
import QuestionnaireStringField from "../components/QuestionnaireStringField";
import QuestionnaireTimeField from "../components/QuestionnaireTimeField";
import QuestionnaireTimestampDisplay from "../components/QuestionnaireTimestampDisplay";
import {
  IntakeFormComponentProps,
  IntakeFormFieldType,
  IntakeFormInitialValuesType,
} from "../types/intake-forms";

export const INTAKE_FORM_INITIAL_VALUES: Partial<IntakeFormInitialValuesType> = {
  [QuestionnaireItemType.string]: "",
  [QuestionnaireItemType.date]: null,
  [QuestionnaireItemType.time]: null,
  [QuestionnaireItemType.openChoice]: {},
  [QuestionnaireItemType.boolean]: true,
  [QuestionnaireItemType.group]: {},
  [QuestionnaireItemType.reference]: null,
  [QuestionnaireItemType.integer]: "",
  [QuestionnaireItemType.display]: "",
  [QuestionnaireItemType.decimal]: "",
  [QuestionnaireItemType.choice]: null,
  [QuestionnaireItemType.attachment]: null,
};

const INTAKE_FORMS_ICONS: Record<string, ReactElement> = {
  calendar: <IconCalendar size={20} />,
  search: <IconSearch size={20} />,
  clock: <IconClock size={20} stroke={1.5} />,
};

export const INTAKE_FORMS_FIELDS: Partial<IntakeFormFieldType> = {
  [QuestionnaireItemType.display]: <T,>({
    key,
    form,
    label,
    enableWhen,
    questionnaireItem,
    formFieldName,
    enableWhenBehavior,
  }: IntakeFormComponentProps<T>) => (
    <EnableWhenWrapper
      key={key}
      form={form}
      enableWhen={enableWhen}
      enableWhenBehavior={enableWhenBehavior}
      parentPath={formFieldName?.split(".").slice(0, -1).join(".")}
    >
      <QuestionnaireDisplay form={form} value={label} questionnaireItem={questionnaireItem} />
    </EnableWhenWrapper>
  ),
  [QuestionnaireItemType.integer]: <T,>({
    key,
    form,
    formFieldName,
    name,
    label,
    required,
    placeholder,
    enableWhen,
    questionnaireItem,
    itemOrder,
    minValue,
    maxValue,
    enableWhenBehavior,
  }: IntakeFormComponentProps<T>) => {
    const allowNegative =
      (getExtensionValue(questionnaireItem, Extensions.QuestionnaireItemAllowNegative) as
        | boolean
        | undefined) ?? true;
    return (
      <EnableWhenWrapper
        key={key}
        form={form}
        enableWhen={enableWhen}
        enableWhenBehavior={enableWhenBehavior}
        parentPath={formFieldName?.split(".").slice(0, -1).join(".")}
      >
        <NumberInput
          name={name}
          min={minValue}
          max={maxValue}
          decimalScale={0}
          required={required}
          allowDecimal={false}
          placeholder={placeholder}
          allowNegative={allowNegative}
          label={itemOrder ? `${itemOrder} - ${label}` : label}
          data-testid={questionnaireFieldTestId(formFieldName)}
          {...form.getInputProps(formFieldName)}
        />
      </EnableWhenWrapper>
    );
  },
  [QuestionnaireItemType.string]: <T,>({
    key,
    form,
    formFieldName,
    name,
    label,
    required,
    placeholder,
    icon,
    mask,
    enableWhen,
    minRange,
    maxRange,
    questionnaireItem,
    itemOrder,
    enableWhenBehavior,
    rightSection,
  }: IntakeFormComponentProps<T>) => (
    <EnableWhenWrapper
      key={key}
      form={form}
      enableWhen={enableWhen}
      enableWhenBehavior={enableWhenBehavior}
      parentPath={formFieldName?.split(".").slice(0, -1).join(".")}
    >
      <QuestionnaireStringField
        name={name}
        mask={mask || ""}
        minRange={minRange}
        maxRange={maxRange}
        withAsterisk={required}
        placeholder={placeholder}
        rightSection={rightSection}
        error={form.errors[formFieldName]}
        questionnaireItem={questionnaireItem!}
        label={itemOrder ? `${itemOrder} - ${label}` : label}
        data-testid={questionnaireFieldTestId(formFieldName)}
        leftSection={typeof icon === "string" ? INTAKE_FORMS_ICONS[icon] : null}
        {...form.getInputProps(formFieldName)}
      />
    </EnableWhenWrapper>
  ),
  [QuestionnaireItemType.date]: <T,>({
    key,
    form,
    formFieldName,
    name,
    label,
    required,
    placeholder,
    icon,
    enableWhen,
    questionnaireItem,
    autoPopulate,
    itemOrder,
    enableWhenBehavior,
    canSelectFutureDates,
  }: IntakeFormComponentProps<T> & { autoPopulate?: boolean }) => {
    return (
      <EnableWhenWrapper
        key={key}
        form={form}
        enableWhen={enableWhen}
        enableWhenBehavior={enableWhenBehavior}
        parentPath={formFieldName?.split(".").slice(0, -1).join(".")}
      >
        <QuestionnaireDateField
          form={form}
          name={name}
          required={required}
          placeholder={placeholder}
          clearable={!autoPopulate}
          autoPopulate={autoPopulate}
          formFieldName={formFieldName}
          canSelectFutureDates={canSelectFutureDates}
          label={itemOrder ? `${itemOrder} - ${label}` : label}
          leftSection={typeof icon === "string" ? INTAKE_FORMS_ICONS[icon] : null}
        />
      </EnableWhenWrapper>
    );
  },
  [QuestionnaireItemType.time]: <T,>({
    key,
    form,
    formFieldName,
    name,
    label,
    required,
    icon,
    enableWhen,
    questionnaireItem,
    itemOrder,
    enableWhenBehavior,
  }: IntakeFormComponentProps<T>) => {
    return (
      <EnableWhenWrapper
        key={key}
        form={form}
        enableWhen={enableWhen}
        enableWhenBehavior={enableWhenBehavior}
        parentPath={formFieldName?.split(".").slice(0, -1).join(".")}
      >
        <QuestionnaireTimeField
          form={form}
          name={name}
          required={required}
          formFieldName={formFieldName}
          label={itemOrder ? `${itemOrder} - ${label}` : label}
          leftSection={typeof icon === "string" ? INTAKE_FORMS_ICONS[icon] : null}
        />
      </EnableWhenWrapper>
    );
  },
  [QuestionnaireItemType.dateTime]: <T,>({
    key,
    form,
    formFieldName,
    label,
    name,
    required,
    enableWhen,
    questionnaireItem,
    questionnaireResponseItem,
    itemOrder,
    enableWhenBehavior,
    autoPopulate,
  }: IntakeFormComponentProps<T>) => {
    // Check for timestamp-display control type
    const formControlCodeableConcept = getExtensionValue(
      questionnaireItem,
      Extensions.QuestionnaireItemControl,
    ) as CodeableConcept | undefined;
    const formControlType = formControlCodeableConcept?.coding?.[0]?.code;

    if (formControlType === "timestamp-display") {
      const timestampEditable = getExtensionValue(
        questionnaireItem,
        Extensions.QuestionnaireFieldTimestampEditable,
      ) as boolean | undefined;

      return (
        <QuestionnaireTimestampDisplay
          key={key}
          form={form}
          autoPopulate={autoPopulate}
          formFieldName={formFieldName}
          editable={timestampEditable === true}
        />
      );
    }

    return (
      <EnableWhenWrapper
        key={key}
        form={form}
        enableWhen={enableWhen}
        enableWhenBehavior={enableWhenBehavior}
        parentPath={formFieldName?.split(".").slice(0, -1).join(".")}
      >
        <QuestionnaireDateTimeField
          key={key}
          form={form}
          name={name}
          required={required}
          formFieldName={formFieldName}
          error={form.errors[formFieldName]}
          questionnaireItem={questionnaireItem}
          label={itemOrder ? `${itemOrder} - ${label}` : label}
          questionnaireResponseItem={questionnaireResponseItem}
        />
      </EnableWhenWrapper>
    );
  },
  [QuestionnaireItemType.choice]: <T,>({
    key,
    form,
    formFieldName,
    name,
    label,
    placeholder,
    questionnaireItem,
    questionnaireResponseItem,
    group,
    enableWhen,
    itemOrder,
    enableWhenBehavior,
    hasCodingInputDetails,
    valueSetIsCreatable,
  }: IntakeFormComponentProps<T>) => (
    <EnableWhenWrapper
      key={key}
      form={form}
      enableWhen={enableWhen}
      enableWhenBehavior={enableWhenBehavior}
      parentPath={formFieldName?.split(".").slice(0, -1).join(".")}
    >
      <QuestionnaireChoiceField
        key={key}
        form={form}
        name={name}
        group={group}
        placeholder={placeholder}
        formFieldName={formFieldName}
        response={questionnaireResponseItem}
        questionnaireItem={questionnaireItem!}
        valueSetIsCreatable={valueSetIsCreatable}
        hasCodingInputDetails={hasCodingInputDetails}
        label={itemOrder ? `${itemOrder} - ${label}` : label}
        onChangeAnswer={(value) => form.getInputProps(formFieldName).onChange(() => value)}
        {...form.getInputProps(formFieldName)}
      />
    </EnableWhenWrapper>
  ),
  [QuestionnaireItemType.boolean]: <T,>({
    name,
    label,
    key,
    formFieldName,
    form,
    enableWhen,
    questionnaireItem,
    itemOrder,
    enableWhenBehavior,
  }: IntakeFormComponentProps<T>) => {
    const checkboxInputProps = form.getInputProps(formFieldName, { type: "checkbox" });
    return (
      <EnableWhenWrapper
        key={key}
        form={form}
        enableWhen={enableWhen}
        enableWhenBehavior={enableWhenBehavior}
        parentPath={formFieldName?.split(".").slice(0, -1).join(".")}
      >
        <Checkbox
          key={key}
          id={name}
          name={name}
          style={{ marginBottom: 15, marginTop: 15 }}
          label={itemOrder ? `${itemOrder} - ${label}` : label}
          data-testid={questionnaireFieldTestId(formFieldName)}
          styles={{
            label: {
              pointerEvents: "none",
            },
          }}
          {...checkboxInputProps}
          checked={checkboxInputProps.checked ?? false}
        />
      </EnableWhenWrapper>
    );
  },
  [QuestionnaireItemType.group]: <T,>({
    form,
    formFieldName,
    group,
    questionnaireResponseItem,
    label,
    key,
    name,
    enableWhen,
    questionnaireItem,
    itemOrder,
    groupTitleOrder,
    enableWhenBehavior,
  }: IntakeFormComponentProps<T>) => (
    <EnableWhenWrapper
      key={key}
      form={form}
      enableWhen={enableWhen}
      enableWhenBehavior={enableWhenBehavior}
      parentPath={formFieldName?.split(".").slice(0, -1).join(".")}
    >
      <QuestionnaireGroupField
        key={key}
        form={form}
        group={group}
        groupLinkId={name}
        formFieldName={formFieldName}
        groupTitleOrder={groupTitleOrder}
        questionnaireResponse={questionnaireResponseItem}
        label={itemOrder ? `${itemOrder} - ${label}` : label}
      />
    </EnableWhenWrapper>
  ),
  [QuestionnaireItemType.reference]: <T,>({
    form,
    formFieldName,
    key,
    name,
    label,
    placeholder,
    enableWhen,
    questionnaireItem,
    itemOrder,
    enableWhenBehavior,
  }: IntakeFormComponentProps<T>) => {
    const { resourceType, searchCriteria, creatable } = getQuestionnaireReferenceFieldConfig(
      questionnaireItem,
      name,
    );

    return (
      <EnableWhenWrapper
        key={key}
        form={form}
        enableWhen={enableWhen}
        enableWhenBehavior={enableWhenBehavior}
        parentPath={formFieldName?.split(".").slice(0, -1).join(".")}
      >
        <QuestionnaireReferenceField
          key={key}
          name={name}
          creatable={creatable}
          placeholder={placeholder}
          resourceType={resourceType}
          searchCriteria={searchCriteria}
          label={itemOrder ? `${itemOrder} - ${label}` : label}
          defaultValue={form.getInputProps(formFieldName).value}
          {...form.getInputProps(formFieldName)}
        />
      </EnableWhenWrapper>
    );
  },
  [QuestionnaireItemType.decimal]: <T,>({
    key,
    form,
    formFieldName,
    name,
    label,
    required,
    placeholder,
    rightSection,
    minRange,
    maxRange,
    minValue,
    maxValue,
    maxDecimalPlaces,
    allowDecimal,
    enableWhen,
    questionnaireItem,
    itemOrder,
    enableWhenBehavior,
  }: IntakeFormComponentProps<T>) => {
    const allowNegative =
      (getExtensionValue(questionnaireItem, Extensions.QuestionnaireItemAllowNegative) as
        | boolean
        | undefined) ?? true;
    const showPlusPrefix =
      (getExtensionValue(questionnaireItem, Extensions.QuestionnaireItemShowPlusPrefix) as
        | boolean
        | undefined) ?? false;
    return (
      <EnableWhenWrapper
        key={key}
        form={form}
        enableWhen={enableWhen}
        enableWhenBehavior={enableWhenBehavior}
        parentPath={formFieldName?.split(".").slice(0, -1).join(".")}
      >
        <QuestionnaireDecimalField<GenericFormValues>
          key={key}
          name={name}
          minRange={minRange}
          maxRange={maxRange}
          minValue={minValue}
          maxValue={maxValue}
          withAsterisk={required}
          placeholder={placeholder}
          rightSection={rightSection}
          allowDecimal={allowDecimal}
          formFieldName={formFieldName}
          allowNegative={allowNegative}
          showPlusPrefix={showPlusPrefix}
          error={form.errors[formFieldName]}
          maxDecimalPlaces={maxDecimalPlaces}
          questionnaireItem={questionnaireItem}
          label={itemOrder ? `${itemOrder} - ${label}` : label}
          data-testid={questionnaireFieldTestId(formFieldName)}
          form={form as unknown as UseFormReturnType<GenericFormValues>}
          {...form.getInputProps(formFieldName)}
        />
      </EnableWhenWrapper>
    );
  },
  [QuestionnaireItemType.attachment]: <T,>({
    key,
    form,
    formFieldName,
    name,
    label,
    required,
    placeholder,
    enableWhen,
    itemOrder,
    enableWhenBehavior,
  }: IntakeFormComponentProps<T>) => (
    <EnableWhenWrapper
      key={key}
      form={form}
      enableWhen={enableWhen}
      enableWhenBehavior={enableWhenBehavior}
      parentPath={formFieldName?.split(".").slice(0, -1).join(".")}
    >
      <QuestionnaireAttachmentField<T>
        key={key}
        form={form}
        required={required}
        placeholder={placeholder}
        formFieldName={formFieldName}
        label={itemOrder ? `${itemOrder} - ${label}` : label}
        error={
          typeof form.errors[formFieldName] === "string" ? form.errors[formFieldName] : undefined
        }
      />
    </EnableWhenWrapper>
  ),
} as const;

export const VALUE_TYPE_PARSERS: Record<
  string,
  (value: string | CodeableConcept | Reference<Resource> | unknown) => unknown
> = {
  valueString: (value) => {
    if (Array.isArray(value)) {
      return value[0] || "";
    }

    if (typeof value === "object" && value && "valueString" in value) {
      return value.valueString;
    }

    return value || "";
  },
  valueDate: (value) => (typeof value === "string" ? parseISO(value) : value),
  valueBoolean: (value) => value,
  valueCoding: (value) => value,
  valueReference: (value) => value,
  valueInteger: (value) => value,
  valueTime: (value) => {
    if (typeof value !== "string") return "";
    const timeMatch = value.match(/^(\d{2}:\d{2})(?::(\d{2}))?$/);
    if (timeMatch) {
      return timeMatch[2] ? value : `${value}:00`;
    }
    return value;
  },
  valueDecimal: (value) => value,
  valueDateTime: (value) => (typeof value === "string" ? value : ""),
  valueAttachment: (value) =>
    typeof value === "object" && value !== null && "url" in value ? value : null,
};
